import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Breakpoints, Color, Grid } from '../../app/StyleGuide';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import { Container, ContainerFluid, PageTitle } from '../../components/Layout';
import LoadingIndicator from '../../components/LoadingIndicator';
import SectionHeader from '../../components/SectionHeader';
import { createCurriculum } from '../../hooks/useCurriculum';
import alertService from '../../services/AlertService';
import curriculumService from '../../services/curriculumService';
import { useUser } from '../authentication';
import {
  BrandCode,
  CurriculumBrand,
  curriculumConfig,
  CurriculumPricing,
  CurriculumPricingEntity,
  Subscription,
} from '../curriculum';
import { Brand } from '../curriculum/models/curriculumPricing';
import BasicPlusPlanCards from './components/BasicPlusPlanCards';
import CurrentSubscriptionCard from './components/CurrentSubscriptionCard';
import CurriculumCard from './components/CurriculumCard';
import SelectMinistrySize from './components/SelectMinistrySize';
import SwitchCurriculumDrawer from './components/SwitchCurriculumDrawer';
import useFetchSubscriptions, { LicenseWithCurriculum } from './hooks/useFetchSubscriptions';
import { findCurriculumForItemNumber } from './utils/findCurriculumForItemNumber';

export type SelectedCurriculum = {
  brand: CurriculumBrand;
  ageGroup: CurriculumPricingEntity;
};

const SwitchCurriculumContainer = styled(ContainerFluid)`
  margin-bottom: ${Grid._14};
`;

const ContentContainer = styled(Container)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${Grid._5};
  opacity: 1;
  transform: translateY(0);
  transition: all 0.5s ease-out;
  overflow: visible;

  @media (min-width: ${Breakpoints.screen_lg}) {
    &:hover .curriculum-card-wrapper {
      opacity: 0.5;
    }

    .curriculum-card-wrapper:hover {
      opacity: 1;
    }
  }

  @media (max-width: ${Breakpoints.screen_md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StepContainer = styled.div<{ isEnabled: boolean }>`
  padding: ${Grid._13} 0;
  opacity: ${props => (props.isEnabled ? 1 : 0.5)};
  pointer-events: ${props => (props.isEnabled ? 'auto' : 'none')};
  transition: opacity 1s ease-in-out;
`;

const CurrentSubscriptionSection = styled.div`
  background-color: ${Color.Gray._10};
  padding: ${Grid._4};
`;

const SelectCurriculumSection = styled.div`
  padding-top: ${Grid._8};
  padding-bottom: ${Grid._13};
`;

export type CurrentSubscriptionData = {
  subscription?: Subscription;
  ageGroup?: string;
  brand?: Brand;
  license?: LicenseWithCurriculum;
};

export function SwitchCurriculum() {
  const { user } = useUser();
  const { activeLicenses, curriculum: curriculumPricing, isLoading: isLoadingSubscriptions } = useFetchSubscriptions();

  const [selectedCurriculum, setSelectedCurriculum] = useState<SelectedCurriculum>();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const [currentSubscriptionData, setCurrentSubscriptionData] = useState<CurrentSubscriptionData>();
  const [selectedMinistrySize, setSelectedMinistrySize] = useState<CurriculumPricingEntity>();
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription>();
  const [curriculumGroups, setCurriculumGroups] = useState<CurriculumPricing[]>();

  const ministrySizeRef = useRef<HTMLDivElement>(null);
  const comparisonRef = useRef<HTMLDivElement>(null);

  const smoothScrollTo = (element: HTMLElement | null, duration: number = 1500) => {
    if (!element) return;

    const targetPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime: number | null = null;

    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function for smoother animation
      const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      window.scrollTo(0, startPosition + distance * easeInOutCubic(progress));

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  const handleCurriculumSelect = (brand: CurriculumBrand, curriculum: CurriculumPricingEntity) => {
    setTimeout(() => {
      setSelectedCurriculum({ brand: brand, ageGroup: curriculum });
      smoothScrollTo(ministrySizeRef.current);
    }, 500);
  };

  const handleCloseDrawer = () => {};

  const handleMinistrySizeSelect = (curriculumEntity: CurriculumPricingEntity) => {
    setSelectedMinistrySize(curriculumEntity);
    smoothScrollTo(comparisonRef.current);
  };

  useEffect(() => {
    if (!subscriptionId || !user?.lastSelectedAccount || !activeLicenses || !curriculumPricing) return;

    const currentLicense = activeLicenses.find(license => license.subscription?.id === subscriptionId);
    const currentSubscriptionItemNumber = currentLicense?.subscription?.item?.number;

    if (!currentSubscriptionItemNumber) {
      alertService.showError('Failed to load current subscription');
      return;
    }
    const currentCurriculum = findCurriculumForItemNumber(curriculumPricing, currentSubscriptionItemNumber);

    setCurrentSubscriptionData({ ...currentCurriculum, license: currentLicense });

    const curriculumLines = curriculumPricing.filter(c =>
      c.curriculum.some(c => !curriculumService.isPreviewCurriculum(c.id))
    );

    setCurriculumGroups(curriculumLines);
  }, [user?.lastSelectedAccount, subscriptionId, activeLicenses, curriculumPricing]);

  return (
    <SwitchCurriculumContainer>
      <>
        <CurrentSubscriptionSection>
          <Container>
            <SectionHeader title="Current Subscription" />
            {isLoadingSubscriptions ? (
              <LoadingIndicator />
            ) : currentSubscriptionData?.brand &&
              currentSubscriptionData.ageGroup &&
              currentSubscriptionData.license?.endDate ? (
              <CurrentSubscriptionCard
                brandName={currentSubscriptionData.brand.name}
                curriculumName={currentSubscriptionData.ageGroup}
                expires={currentSubscriptionData.license?.endDate}
                brandCode={currentSubscriptionData.brand.code as BrandCode}
              />
            ) : (
              <HelpAndInfoBox title="Failed to load current subscription" description="Please try again later." />
            )}
          </Container>
        </CurrentSubscriptionSection>

        {!isLoadingSubscriptions && currentSubscriptionData?.subscription && (
          <Container>
            <SelectCurriculumSection>
              <PageTitle style={{ marginBottom: Grid._8 }}>
                <h1>Select Curriculum</h1>
                <p>Select the curriculum you want to switch to.</p>
              </PageTitle>
              <ContentContainer>
                {curriculumGroups
                  ?.filter(c => curriculumConfig.standardCurriculumLines.includes(c.brand?.code as BrandCode))
                  .map(c => [c, createCurriculum(c.brand.code as BrandCode)?.brand] as const)
                  .map(
                    ([curriculum, brand], index) =>
                      curriculum &&
                      brand && (
                        <CurriculumCard
                          curriculumGroup={curriculum}
                          brand={brand}
                          key={brand.code}
                          index={index + 1}
                          curriculumPackageName={currentSubscriptionData?.ageGroup}
                          selectedButton={selectedCurriculum}
                          onButtonClick={handleCurriculumSelect}
                          isCurrentSubscription={
                            currentSubscriptionData?.brand?.code?.toLowerCase() === brand.code.toLowerCase()
                          }
                        />
                      )
                  )}
              </ContentContainer>
            </SelectCurriculumSection>
          </Container>
        )}
        <div ref={ministrySizeRef}>
          {!!selectedCurriculum && (
            <StepContainer isEnabled={!!selectedCurriculum} style={{ backgroundColor: Color.Gray._10 }}>
              <Container>
                <PageTitle style={{ marginBottom: Grid._8 }}>
                  <h1>Select Ministry Size</h1>
                  <p>
                    How many average attendees (leaders + participants) will use Bible Studies for Life for Students
                    each week?
                  </p>
                </PageTitle>
                <SelectMinistrySize
                  selectedCurriculum={selectedCurriculum}
                  onSelect={handleMinistrySizeSelect}
                  currentMinistrySize={currentSubscriptionData?.subscription?.size}
                />
              </Container>
            </StepContainer>
          )}
        </div>
        <div ref={comparisonRef}>
          {!!selectedCurriculum && (
            <StepContainer isEnabled={!!selectedMinistrySize}>
              <Container>
                <PageTitle style={{ marginBottom: Grid._4 }}>
                  <h1>Compare Plans</h1>
                  <p>Compare the plans you want to switch to.</p>
                </PageTitle>

                {selectedMinistrySize && currentSubscriptionData?.subscription && (
                  <BasicPlusPlanCards
                    selectedCurriculum={selectedCurriculum}
                    selectedMinistrySize={selectedMinistrySize}
                    currentSelectedPeriod={currentSubscriptionData?.subscription?.period}
                    currentSelectedPackage={currentSubscriptionData?.subscription?.package}
                    onPlanSelect={subscription => {
                      setSelectedSubscription(subscription);
                    }}
                  />
                )}
              </Container>
            </StepContainer>
          )}
        </div>
        {currentSubscriptionData?.subscription && (
          <SwitchCurriculumDrawer
            currentSubscriptionData={currentSubscriptionData}
            selectedCurriculum={selectedCurriculum}
            selectedMinistrySize={selectedMinistrySize}
            selectedSubscription={selectedSubscription}
            handleCloseDrawer={handleCloseDrawer}
          />
        )}
      </>
    </SwitchCurriculumContainer>
  );
}
